import { currencyFormatter } from "@fla/common";
import {
  AdditionalRequirementsPaymentProps,
  AdditionalRequirementsStep01Schema,
  AdditionalRequirements_InitialState,
  Button,
  EditIcon,
  FormError,
  RadioSelect,
  useNames,
  useToggle
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useState } from "react";
import { UseFormReset, useFieldArray, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import AddPayment from "./AddPayment";

interface IProps {
  formId: string;
  store: AdditionalRequirements_InitialState;
  onSubmit: (values: AdditionalRequirements_InitialState) => void;
}
export default function PaymentDetails({ formId, store, onSubmit }: IProps) {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const { spouseFirstName, getName } = useNames({ initialDetails });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors }
  } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(AdditionalRequirementsStep01Schema)
  });

  const { toggleOn, onToggleClick } = useToggle();
  const [paymentEditData, setPaymentEditData] = useState<
    Partial<AdditionalRequirementsPaymentProps> & { id: number }
  >();
  const { append, remove, update } = useFieldArray({
    control,
    name: "payments"
  });
  const closePaymentDetailsModal = () => {
    onToggleClick();
    setPaymentEditData(undefined);
  };

  const addPaymentDetails = () => {
    setPaymentEditData(undefined);
    onToggleClick();
  };

  const onSubmitPaymentDetails = (
    values: AdditionalRequirementsPaymentProps,
    reset: UseFormReset<AdditionalRequirementsPaymentProps>
  ) => {
    if (paymentEditData) {
      update(paymentEditData.id, values);
    } else {
      append(values);
    }
    closePaymentDetailsModal();
    reset();
  };

  const paymentDetailsProps = {
    isOpen: toggleOn,
    onClose: closePaymentDetailsModal,
    remove,
    editData: paymentEditData,
    onSubmitPaymentDetails
  };

  return (
    <>
      <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <RadioSelect
          title="Do you require any additional payments to be made?"
          subTitle="Eg A payment from John to Jane to reimburse her for the household furniture"
          name="if_additional_payments"
          id="if_additional_payments"
          control={control}
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" }
          ]}
          type="card"
          theme="dark"
        />
        {watch("if_additional_payments") === "yes" ? (
          <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
            <p className="px-16 text-center text-base font-bold">
              Please add all the all the other debts both you and&nbsp;
              {spouseFirstName}&nbsp;have in Australia
            </p>
            <Button className="px-5 py-2 uppercase" fontSize="sm" onClick={addPaymentDetails}>
              + Add Payment
            </Button>
          </div>
        ) : null}
        {errors?.payments?.message ? (
          <section className="w-fit mx-auto">
            <FormError error={errors?.payments?.message || ""} />
          </section>
        ) : null}

        {Array.isArray(watch("payments")) && watch("payments")!.length
          ? watch("payments")!.map((p, idx) => (
              <div key={+idx} className="rounded-lg shadow-md bg-white p-5 mb-5 grid grid-cols-8 gap-y-4 md:gap-y-0">
                <div className="col-span-2 md:col-span-1 flex items-center">
                  <div className="w-12 h-12 bg-gray-300 rounded-full flex justify-center items-center text-gray-500 text-2xl">
                    $
                  </div>
                </div>
                <div className="col-span-6 md:col-span-5 pr-5">
                  <div className="flex justify-between items-center">
                    <p className="text-lg font-semibold">
                      Payment from {getName(p.payor)} to&nbsp;
                      {p.other_person_entity || getName(p.payee)}
                    </p>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500 line-clamp-3">{p.description}</p>
                  </div>
                </div>
                <div className="col-span-8 md:col-span-2 flex flex-col items-end">
                  <p className="text-xl">{currencyFormatter.format(Number(p.value))}</p>
                  <button
                    className="transition-all hover:bg-primary/20 px-2 py-1 rounded-md text-sm text-primary font-bold uppercase mt-4 flex items-center space-x-2"
                    onClick={() => {
                      setPaymentEditData({
                        ...p,
                        id: idx
                      });
                      onToggleClick();
                    }}
                    type="button"
                  >
                    <EditIcon classes="w-3.5 h-3.5" />
                    <p className="tracking-wide">Edit</p>
                  </button>
                </div>
              </div>
            ))
          : null}
      </form>
      <AddPayment {...paymentDetailsProps} />
    </>
  );
}
